import { useCallback, useState, type ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useIdleTimeout } from "@/hooks/useIdleTimeout";
import { useAdminRole, canManageAdmins } from "@/hooks/useAdminRole";
import { useAdminThreadUnread, useAdminRealtimeBus } from "@/hooks/useAdminThreadUnread";
import { Logo } from "./Logo";
import { IdleWarningDialog } from "./IdleWarningDialog";

// Idle auto-logoff. HIPAA technical safeguards (45 CFR 164.312(a)(2)(iii))
// require automatic logoff but don't pin a duration — industry norm for
// active clinical / admin workstations is 15–30 min. We sit at 25 min so
// the care team isn't kicked mid-conversation while still bounding the
// exposure window if someone walks away from an unlocked screen.
const IDLE_MINUTES = 25;
const IDLE_WARN_SECONDS = 60;

// Tabs are role-gated. The "Admins" tab only renders for super admins.
type Tab = { label: string; to: string; superOnly?: boolean };
const TABS: Tab[] = [
  { label: "Users",     to: "/admin/users" },
  { label: "Messages",  to: "/admin/messages" },
  { label: "Refills",   to: "/admin/refills" },
  { label: "Analytics", to: "/admin/analytics" },
  { label: "Admins",    to: "/admin/admins", superOnly: true },
  { label: "Audit log", to: "/admin/audit-log", superOnly: true },
];

export function AdminLayout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const { role } = useAdminRole();
  const visibleTabs = TABS.filter((t) => !t.superOnly || canManageAdmins(role));

  // Single shared Realtime subscription for the admin shell — every
  // page benefits without each route opening its own socket.
  useAdminRealtimeBus();
  const { count: unreadThreads } = useAdminThreadUnread();

  async function handleSignOut() {
    setSigningOut(true);
    await supabase.auth.signOut();
    navigate("/admin/login", { replace: true });
  }

  const handleIdleLogout = useCallback(async () => {
    await supabase.auth.signOut();
    navigate("/admin/login", { replace: true, state: { idleLogout: true } });
  }, [navigate]);
  const idle = useIdleTimeout({
    idleMinutes: IDLE_MINUTES,
    warnSecondsBefore: IDLE_WARN_SECONDS,
    onIdle: handleIdleLogout,
  });

  return (
    <div className="min-h-screen bg-fh-hero flex flex-col">
      {/* Steel header mirrors the patient sidebar per the brand refresh. */}
      <header className="bg-fh-steel border-b border-fh-border">
        <div className="mx-auto max-w-[1280px] px-5 md:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size="sm" />
            <span className="text-[12px] font-semibold tracking-[0.14em] uppercase text-fh-gray border-l border-fh-border pl-3 ml-1">
              Admin
            </span>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            disabled={signingOut}
            className="text-sm font-medium text-fh-gray hover:text-fh-ink"
          >
            {signingOut ? "Signing out…" : "Sign out"}
          </button>
        </div>
        <nav className="mx-auto max-w-[1280px] px-5 md:px-8 flex gap-1 -mb-px">
          {visibleTabs.map((t) => {
            const showBadge = t.to === "/admin/messages" && unreadThreads > 0;
            return (
              <NavLink
                key={t.to}
                to={t.to}
                end
                className={({ isActive }) =>
                  [
                    "px-3 py-2.5 text-[13.5px] font-semibold border-b-2 -mb-px transition flex items-center gap-1.5",
                    isActive
                      ? "text-fh-green-dark border-fh-green"
                      : "text-fh-gray border-transparent hover:text-fh-ink",
                  ].join(" ")
                }
              >
                {t.label}
                {showBadge && (
                  <span
                    aria-label={`${unreadThreads} unread`}
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-fh-green-dark text-white text-[10.5px] font-bold leading-none"
                  >
                    {unreadThreads > 99 ? "99+" : unreadThreads}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </header>
      <main className="flex-1 mx-auto w-full max-w-[1280px] px-5 md:px-8 py-8">
        {children}
      </main>

      <IdleWarningDialog
        open={idle.warning}
        secondsLeft={idle.secondsLeft}
        onStay={idle.stayActive}
      />
    </div>
  );
}
